import { isUserAuthorized } from '@src/hooks/useRoleAuth';
import {
  ACTION,
  canExecuteOtherUserNegotiatedRFQ,
  isValidQuotePrice,
  QuoteStatusEnum,
  SideEnum,
  type Quote,
  type QuoteMarket,
  type User,
} from '@talos/kyoko';
import { calculateBestQuotes, type QuoteMarketWithBestBidOffer, type RfqBestQuoteBy } from '../../../utils/rfq';

/** Sell actions consume the market bid; buy actions consume the market offer. */
export function getQuoteTradeSidePrice(
  quoteMarket: Pick<QuoteMarket, 'BidPx' | 'OfferPx'> | undefined,
  side: SideEnum
): string | undefined {
  return side === SideEnum.Sell ? quoteMarket?.BidPx : quoteMarket?.OfferPx;
}

/**
 * Resolves why a Quotes V2 trading action cannot trade a specific side.
 *
 * The action is disabled unless the quote and quote market are open, the relevant side has a price, the current
 * user can accept RFQs, the quote belongs to that user (unless cross-user negotiated RFQ trading is enabled), and any
 * parent care-order side matches the requested side.
 */
export function getQuoteTradeSideDisabledReason({
  quote,
  quoteMarket,
  side,
  user,
  careOrderSide,
  isMobile,
  requireMobileUITraderRole = false,
  sweepGroupIDQuotesOnlySweepable = false,
  enableExecutingOtherUserNegotiatedRFQ = false,
}: {
  quote: Pick<Quote, 'PricingMode' | 'QuoteStatus' | 'RFQTradingProtocol' | 'SweepGroupID' | 'User'>;
  quoteMarket: Pick<QuoteMarket, 'BidPx' | 'OfferPx' | 'QuoteStatus'> | undefined;
  side: SideEnum;
  user: User;
  careOrderSide?: SideEnum;
  isMobile?: boolean;
  requireMobileUITraderRole?: boolean;
  sweepGroupIDQuotesOnlySweepable?: boolean;
  enableExecutingOtherUserNegotiatedRFQ?: boolean;
}): string | undefined {
  const price = getQuoteTradeSidePrice(quoteMarket, side);
  const hasPrice =
    price != null && price !== '' && isValidQuotePrice(price, quoteMarket?.QuoteStatus, quote.PricingMode);
  const canAccept = isUserAuthorized(user, ACTION.ACCEPT_RFQ, isMobile, undefined, requireMobileUITraderRole);
  const isOwnQuote = quote.User === user.Name;
  const canTradeOtherUserNegotiatedRFQ = canExecuteOtherUserNegotiatedRFQ({
    rfqTradingProtocol: quote.RFQTradingProtocol,
    enableExecutingOtherUserNegotiatedRFQ,
  });

  if (quote.QuoteStatus !== QuoteStatusEnum.Open) {
    return 'Quote is not open.';
  }

  if (quoteMarket?.QuoteStatus !== QuoteStatusEnum.Open) {
    return 'Quote market is not open.';
  }

  if (!hasPrice) {
    return `No valid ${side === SideEnum.Sell ? 'bid' : 'offer'} price is available.`;
  }

  if (!canAccept) {
    return 'You are not authorized to accept RFQs.';
  }

  if (!isOwnQuote && !canTradeOtherUserNegotiatedRFQ) {
    return 'Quote belongs to another user.';
  }

  if (careOrderSide != null && careOrderSide !== side) {
    return `Parent care order only allows ${careOrderSide.toLowerCase()} actions.`;
  }

  if (sweepGroupIDQuotesOnlySweepable && quote.SweepGroupID) {
    return 'Quote is part of a sweep group and can only be traded from the sweeping panel.';
  }

  return undefined;
}

export function getQuoteOpenButtonDisabledReason({
  quote,
  user,
  sweepGroupIDQuotesOnlySweepable = false,
  enableExecutingOtherUserNegotiatedRFQ = false,
}: {
  quote: Quote;
  user: User;
  sweepGroupIDQuotesOnlySweepable?: boolean;
  enableExecutingOtherUserNegotiatedRFQ?: boolean;
}): string | undefined {
  const isOwnQuote = quote.User === user.Name;
  const canOpenOtherUserNegotiatedRFQ = canExecuteOtherUserNegotiatedRFQ({
    rfqTradingProtocol: quote.RFQTradingProtocol,
    enableExecutingOtherUserNegotiatedRFQ,
  });

  if (!quote.canOpenQuote) {
    return 'Quote cannot be opened.';
  }

  if (!isOwnQuote && !canOpenOtherUserNegotiatedRFQ) {
    return 'Quote belongs to another user.';
  }

  if (sweepGroupIDQuotesOnlySweepable && quote.SweepGroupID) {
    return 'Quote is part of a sweep group and can only be opened from the sweeping panel.';
  }

  return undefined;
}

/**
 * Looks into Quote.Markets and grabs the relevant markets to elevate into the QuoteRow for the QuotesBlotter
 *
 * If filled / pending fix, will grab the bid-offer for whatever market we traded on (even if it wasn't the best price). Will
 * still flag the traded market with isBestBid, isBestOffer correctly despite it being a slightly different codepath
 *
 * Otherwise, will elevate the best open bid-offers (which can be on separate markets)
 *
 */
export function getQuoteMarketsForQuoteRow(
  quote: Quote,
  bestBy?: RfqBestQuoteBy
): {
  bid: QuoteMarketWithBestBidOffer | undefined;
  offer: QuoteMarketWithBestBidOffer | undefined;
} {
  // If the quote is filled (we have a traded market), then we always surface the traded market's pricing details into the quote row
  if (quote.tradedMarket) {
    // False here means that we calculate prices for all markets, not just open markets
    const bests = calculateBestQuotes({
      quotes: quote.Markets,
      pricingMode: quote.PricingMode,
      onlyActive: false,
      bestBy,
    });
    const isBestBid = bests.find(m => m.isBestBid)?.Market === quote.tradedMarket.Market;
    const isBestOffer = bests.find(m => m.isBestOffer)?.Market === quote.tradedMarket.Market;

    return {
      bid: { ...quote.tradedMarket, isBestBid, isBestOffer },
      offer: { ...quote.tradedMarket, isBestBid, isBestOffer },
    };
  }

  // Else down here there is no traded market. We are either rejected (terminal), pending new, or open...
  // We just elevate whichever is the best bid/offers
  const openBests = calculateBestQuotes({
    quotes: quote.Markets,
    pricingMode: quote.PricingMode,
    onlyActive: true, // true = only calculate based on Open market quotes
    bestBy,
  });
  const bestBid = openBests.find(m => m.isBestBid);
  const bestOffer = openBests.find(m => m.isBestOffer);

  // If there is no bestBid or bestOffer yet, we are OK with returning undefined here. Renders as empty cells.
  return { bid: bestBid, offer: bestOffer };
}
